import React, { useState } from 'react';
import { Pencil, Trash2, X } from 'lucide-react';
import ConfirmModal from '../utils/ConfirmModal';
import '../../component-styles/ManageSchedulesModal.css';
import API_BASE from '../../utils/api.js';

export default function ManageSchedulesModal({
  userId,
  projectId,
  schedules,
  onChange,
  onScheduleDeleted,
  onClose,
}) {
  const [newName, setNewName] = useState('');
  const [adding, setAdding] = useState(false);
  const [renamingSk, setRenamingSk] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const [renaming, setRenaming] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [error, setError] = useState(null);

  function sortByName(list) {
    return [...list].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }

  async function handleAdd() {
    const name = newName.trim();
    if (!name) return setError('Schedule name is required.');

    setAdding(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/schedules/${encodeURIComponent(userId)}/${encodeURIComponent(projectId)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Failed to create schedule');

      onChange(sortByName([...schedules, data]));
      setNewName('');
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setAdding(false);
    }
  }

  function startRename(schedule) {
    setRenamingSk(schedule.sk);
    setRenameValue(schedule.name);
    setError(null);
  }

  async function handleRename(sk) {
    const name = renameValue.trim();
    if (!name) return setError('Schedule name is required.');

    setRenaming(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/schedules/${encodeURIComponent(userId)}/${encodeURIComponent(sk)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Failed to rename schedule');

      onChange(sortByName(schedules.map((s) => (s.sk === sk ? data : s))));
      setRenamingSk(null);
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setRenaming(false);
    }
  }

  async function handleDelete(schedule) {
    try {
      await fetch(`${API_BASE}/api/schedules/${encodeURIComponent(userId)}/${encodeURIComponent(schedule.sk)}`, {
        method: 'DELETE',
      });
      onChange(schedules.filter((s) => s.sk !== schedule.sk));
      onScheduleDeleted?.();
    } catch (err) {
      console.error('Failed to delete schedule:', err);
    } finally {
      setDeleteTarget(null);
    }
  }

  return (
    <div className="msm-overlay" onClick={onClose}>
      <div className="msm-modal" onClick={(e) => e.stopPropagation()}>
        <button className="msm-close" onClick={onClose} aria-label="Close">
          <X size={18} />
        </button>
        <h3 className="msm-heading">Manage Schedules</h3>

        <div className="msm-list">
          {schedules.length === 0 && <div className="msm-empty">No schedules yet.</div>}
          {schedules.map((s) => (
            <div className="msm-row" key={s.sk}>
              {renamingSk === s.sk ? (
                <>
                  <input
                    className="msm-input"
                    type="text"
                    value={renameValue}
                    autoFocus
                    onChange={(e) => setRenameValue(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleRename(s.sk)}
                  />
                  <div className="msm-row-actions">
                    <button onClick={() => handleRename(s.sk)} disabled={renaming}>
                      Save
                    </button>
                    <button onClick={() => setRenamingSk(null)} disabled={renaming}>
                      Cancel
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <span className="msm-row-name">{s.name}</span>
                  <div className="msm-row-icons">
                    <button
                      type="button"
                      className="msm-icon-btn"
                      onClick={() => startRename(s)}
                      aria-label={`Rename ${s.name}`}
                    >
                      <Pencil size={14} />
                    </button>
                    <button
                      type="button"
                      className="msm-icon-btn"
                      onClick={() => setDeleteTarget(s)}
                      aria-label={`Delete ${s.name}`}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>

        {error && <div className="msm-error">{error}</div>}

        <div className="msm-add-row">
          <input
            className="msm-input"
            type="text"
            value={newName}
            placeholder="New schedule name"
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
          />
          <button className="msm-add-btn" onClick={handleAdd} disabled={adding}>
            {adding ? 'Adding…' : 'Add'}
          </button>
        </div>
      </div>

      <ConfirmModal
        isOpen={Boolean(deleteTarget)}
        title="Delete Schedule"
        message={`Delete "${deleteTarget?.name || 'this schedule'}"? Items using it will become Unassigned.`}
        confirmLabel="Delete"
        destructive
        onConfirm={() => handleDelete(deleteTarget)}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
